import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type SubjectBelief, type SubjectPageResponse } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";

/**
 * UI-10 on a phone — one subject, read rather than worked on.
 *
 * The same belief renderer's *content* as the desktop page, with the affordances the
 * handoff drops: no right rail, no page history, no wiki path, no "the words behind this"
 * disclosure. Provenance is one quiet line naming where the belief was said, because on a
 * phone the question is what is held, and the answer to "says who" is a tap away on the
 * desktop rather than a popover over 390 px of text.
 *
 * Superseded beliefs are not drawn here at all. The desktop page has a toggle for them; the
 * handoff's phone reader has neither, and WORLD-1 is clear that a closed window is not what
 * the world view currently holds.
 */
export function PhoneSubject() {
  const { slug = "" } = useParams();
  const { api } = useApp();

  const [answer, setAnswer] = useState<SubjectPageResponse | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAnswer(await api.getSubject(slug));
      setProblem(null);
    } catch (failure) {
      setAnswer(null);
      setProblem(
        failure instanceof ApiError && failure.status === 404
          ? "There is no subject at that address."
          : "That subject could not be read.",
      );
    } finally {
      setLoading(false);
    }
  }, [api, slug]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="p-4 text-[14px] text-[var(--text-muted)]">Loading…</p>;

  if (!answer) {
    return (
      <p role="alert" className="p-4 text-[14px] text-[var(--warning)]">
        {problem}
      </p>
    );
  }

  const { page } = answer;
  const meta = subjectKindMeta(page.kind);
  const held = page.sections.reduce((total, section) => total + section.beliefs.length, 0);

  return (
    <div className="flex flex-col gap-5 p-4 [animation:omFadeUp_220ms_cubic-bezier(0.4,0,0.2,1)_both]">
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          {meta.label}
        </span>
        {(page.about ?? page.summary) && (
          <span className="text-[14px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
            {page.about ?? page.summary}
          </span>
        )}
        <span className="text-[12px] text-[var(--text-muted)]">
          {held} {held === 1 ? "belief" : "beliefs"} · updated {formatRelative(page.updatedAt)}
        </span>
      </div>

      {held === 0 && (
        <p className="text-[14px] text-[var(--text-secondary)]">Nothing is held on this subject yet.</p>
      )}

      {page.sections.map((section) => (
        <section key={section.anchor} className="flex flex-col gap-3.5">
          <h2 className="border-b border-[var(--border-subtle)] pb-1.5 text-[15px] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            {section.heading}
          </h2>
          {section.beliefs.map((belief) => (
            <Belief key={belief.id} belief={belief} />
          ))}
        </section>
      ))}
    </div>
  );
}

function Belief({ belief }: { belief: SubjectBelief }) {
  // The two flags the data can prove, drawn exactly as the desktop page draws them: a
  // belief in an unresolved contradiction (AUTO-1), and one a `weaken` has driven down.
  const uncertain = belief.confidence !== null && belief.confidence < 0.5;
  const flag = belief.contradicted
    ? { label: "Contradicted", colour: "var(--destructive)" }
    : uncertain
      ? { label: "Uncertain", colour: "var(--warning)" }
      : null;

  // One name and a count, rather than a list: "Weekly standup +2" is what a 390 px line can
  // carry and still be read.
  const sources = [...new Map(belief.insights.map((insight) => [insight.sourceMaterialId, insight])).values()];
  const first = sources[0];
  const extra = sources.length - 1;

  return (
    <div className="flex flex-col gap-[7px]">
      {flag && (
        <span
          className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-full)] border px-2.5 py-0.5 text-[11px] font-semibold"
          style={{ borderColor: flag.colour, color: flag.colour }}
        >
          <span className="size-[5px] rounded-full" style={{ background: flag.colour }} aria-hidden />
          {flag.label}
        </span>
      )}

      <p className="text-[16px] leading-[1.5] text-[var(--text-primary)] [text-wrap:pretty]">
        {belief.statement}
      </p>

      {first && (
        <p className="text-[12px] text-[var(--text-muted)]">
          {first.sourceTitle ?? "Untitled source"}
          {extra > 0 && ` +${extra}`}
          {first.sourceDate && ` · ${formatDate(first.sourceDate)}`}
        </p>
      )}
    </div>
  );
}
